import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  challengeService,
  Challenge,
  ChallengeListResponse,
} from "../services/challengeService";
import MainHeader from "../components/MainHeader";
import ConfirmationModal from "../common/ConfirmationModal";
import { toast } from "react-toastify";

interface ChallengeManagementListProps {
  onViewChallenge: (challenge: Challenge) => void;
  onEditChallenge: (challenge: Challenge) => void;
  onCloneChallenge: (challenge: Challenge) => void;
  onCreateChallenge: () => void;
  onBackToDashboard: () => void;
}

// Custom hook for debouncing
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function ChallengeManagementList({
  onViewChallenge,
  onEditChallenge,
  onCreateChallenge,
  onBackToDashboard,
  onCloneChallenge,
}: ChallengeManagementListProps) {
  const [challengesResponse, setChallengesResponse] =
    useState<ChallengeListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [experienceFilter, setExperienceFilter] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [guaranteedFilter, setGuaranteedFilter] = useState("all");
  const [expandedRows, setExpandedRows] = useState<{ [id: string]: boolean }>(
    {}
  );
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [challengeToComplete, setChallengeToComplete] =
    useState<Challenge | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [challengeToDelete, setChallengeToDelete] = useState<Challenge | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [challengeToCancel, setChallengeToCancel] = useState<Challenge | null>(
    null
  );
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [challengeToPublish, setChallengeToPublish] =
    useState<Challenge | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [challengeToStart, setChallengeToStart] = useState<Challenge | null>(
    null
  );
  const [startLoading, setStartLoading] = useState(false);

  // Debounce search term to prevent excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const fetchChallenges = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await challengeService.getChallenges(
        currentPage,
        perPage,
        debouncedSearchTerm || undefined,
        statusFilter !== "all" ? statusFilter : undefined,
        typeFilter !== "all" ? typeFilter : undefined,
        categoryFilter !== "all" ? categoryFilter : undefined,
        experienceFilter !== "all" ? experienceFilter : undefined,
        riskFilter !== "all" ? riskFilter : undefined,
        guaranteedFilter !== "all" ? guaranteedFilter : undefined
      );
      setChallengesResponse(response);
    } catch (err) {
      console.error("Failed to fetch challenges:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch challenges"
      );
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    perPage,
    debouncedSearchTerm,
    statusFilter,
    typeFilter,
    categoryFilter,
    experienceFilter,
    riskFilter,
    guaranteedFilter,
  ]);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  const handleStatusAction = async (
    challengeId: string,
    action: "publish" | "cancel" | "start" | "complete"
  ) => {
    try {
      setActionLoading(challengeId);
      switch (action) {
        case "publish":
          await challengeService.publishChallenge(challengeId);
          break;
        case "cancel":
          await challengeService.cancelChallenge(challengeId);
          break;
        case "start":
          await challengeService.startChallenge(challengeId);
          break;
        case "complete":
          await challengeService.completeChallenge(challengeId);
          break;
      }
      fetchChallenges();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : `Failed to ${action} challenge`
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteChallenge = (challenge: Challenge) => {
    setChallengeToDelete(challenge);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!challengeToDelete) return;

    try {
      setDeleteLoading(true);
      await challengeService.deleteChallenge(challengeToDelete.id);
      setShowDeleteModal(false);
      setChallengeToDelete(null);
      fetchChallenges();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete challenge"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setChallengeToDelete(null);
  };

  const handleCancelChallenge = (challenge: Challenge) => {
    setChallengeToCancel(challenge);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!challengeToCancel) return;

    try {
      setCancelLoading(true);
      await challengeService.cancelChallenge(challengeToCancel.id);
      setShowCancelModal(false);
      setChallengeToCancel(null);
      fetchChallenges();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to cancel challenge"
      );
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCancelCancel = () => {
    setShowCancelModal(false);
    setChallengeToCancel(null);
  };

  const handlePublishChallenge = (challenge: Challenge) => {
    setChallengeToPublish(challenge);
    setShowPublishModal(true);
  };

  const handleConfirmPublish = async () => {
    if (!challengeToPublish) return;

    try {
      setPublishLoading(true);
      await challengeService.publishChallenge(challengeToPublish.id);
      setShowPublishModal(false);
      setChallengeToPublish(null);
      fetchChallenges();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to publish challenge"
      );
    } finally {
      setPublishLoading(false);
    }
  };

  const handleCancelPublish = () => {
    setShowPublishModal(false);
    setChallengeToPublish(null);
  };

  const handleStartChallenge = (challenge: Challenge) => {
    setChallengeToStart(challenge);
    setShowStartModal(true);
  };

  const handleConfirmStart = async () => {
    if (!challengeToStart) return;

    try {
      setStartLoading(true);
      await challengeService.startChallenge(challengeToStart.id);
      setShowStartModal(false);
      setChallengeToStart(null);
      fetchChallenges();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to start challenge"
      );
    } finally {
      setStartLoading(false);
    }
  };

  const handleCancelStart = () => {
    setShowStartModal(false);
    setChallengeToStart(null);
  };

  const handleCompleteChallenge = (challenge: Challenge) => {
    setChallengeToComplete(challenge);
    setShowCompleteModal(true);
  };

  const handleConfirmComplete = async () => {
    if (!challengeToComplete) return;

    try {
      setActionLoading(challengeToComplete.id);
      await challengeService.completeChallenge(challengeToComplete.id);
      setShowCompleteModal(false);
      setChallengeToComplete(null);
      fetchChallenges();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to complete challenge"
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelComplete = () => {
    setShowCompleteModal(false);
    setChallengeToComplete(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startFormatted = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    const endFormatted = end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${startFormatted} - ${endFormatted}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };
  const handleSafePublish = (challenge: Challenge) => {
    const start = new Date(challenge.start_date);
    const end = new Date(challenge.end_date);

    if (start < now) {
      toast.error("Cannot publish: Start time has already passed");
      return;
    }
    if (end < now) {
      toast.error("Cannot publish: End time has already passed");
      return;
    }
    handlePublishChallenge(challenge); // your existing publish logic
  };
  const getStatusActionButton = (challenge: Challenge) => {
    const isLoading = actionLoading === challenge.id;

    switch (challenge.status) {
      case "Draft":
        return (
          <button
            onClick={() => handleSafePublish(challenge)}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors duration-200"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
            ) : (
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
            Publish
          </button>
        );
      case "Upcoming":
        return (
          <button
            onClick={() => handleStartChallenge(challenge)}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
            ) : (
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
            Start
          </button>
        );
      case "Live":
        return (
          <button
            onClick={() => handleCompleteChallenge(challenge)}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-colors duration-200"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
            ) : (
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            )}
            Complete
          </button>
        );
      default:
        return null;
    }
  };

  const getStatusCancelButton = (challenge: Challenge) => {
    if (challenge.status === "Completed" || challenge.status === "Cancelled") {
      return null;
    }

    const isLoading = actionLoading === challenge.id;

    return (
      <button
        onClick={() => handleCancelChallenge(challenge)}
        disabled={isLoading}
        className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors duration-200"
      >
        {isLoading ? (
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-1"></div>
        ) : (
          <svg
            className="w-3 h-3 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
        Cancel
      </button>
    );
  };
  const getCloneButton = (challenge: Challenge) => {
    const isLoading = actionLoading === challenge.id;

    return (
      //   <motion.button
      //   onClick={() => onCloneChallenge(challenge)}
      //   disabled={isLoading}
      //   className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-orange-400 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 transition-colors duration-200"
      // >
      //   {isLoading ? (
      //     <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
      //   ) : (
      //     <svg
      //       xmlns="http://www.w3.org/2000/svg"
      //       className="w-4 h-4 mr-1"
      //       viewBox="0 0 24 24"
      //       fill="none"
      //       stroke="currentColor"  // inherits button text color
      //       strokeWidth="2"
      //       strokeLinecap="round"
      //       strokeLinejoin="round"
      //     >
      //       <path d="M8 8H7.2C6.0799 8 5.51984 8 5.09202 8.21799C4.71569 8.40973 4.40973 8.71569 4.21799 9.09202C4 9.51984 4 10.0799 4 11.2V16.8C4 17.9201 4 18.4802 4.21799 18.908C4.40973 19.2843 4.71569 19.5903 5.09202 19.782C5.51984 20 6.0799 20 7.2 20H12.8C13.9201 20 14.4802 20 14.908 19.782C15.2843 19.5903 15.5903 19.2843 15.782 18.908C16 18.4802 16 17.9201 16 16.8V16M11.2 16H16.8C17.9201 16 18.4802 16 18.908 15.782C19.2843 15.5903 19.5903 15.2843 19.782 14.908C20 14.4802 20 13.9201 20 12.8V7.2C20 6.0799 20 5.51984 19.782 5.09202C19.5903 4.71569 19.2843 4.40973 18.908 4.21799C18.4802 4 17.9201 4 16.8 4H11.2C10.0799 4 9.51984 4 9.09202 4.21799C8.71569 4.40973 8.40973 4.71569 8.21799 5.09202C8 5.51984 8 6.07989 8 7.2V12.8C8 13.9201 8 14.4802 8.21799 14.908C8.40973 15.2843 8.71569 15.5903 9.09202 15.782C9.51984 16 10.0799 16 11.2 16Z" />
      //     </svg>
      //   )}
      // </motion.button>
      <motion.button
        onClick={() => onCloneChallenge(challenge)}
        className="text-orange-600 hover:text-orange-900 transition-colors duration-150"
        title="Clone Challenge"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-4 h-4 mr-1"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor" // inherits button text color
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M8 8H7.2C6.0799 8 5.51984 8 5.09202 8.21799C4.71569 8.40973 4.40973 8.71569 4.21799 9.09202C4 9.51984 4 10.0799 4 11.2V16.8C4 17.9201 4 18.4802 4.21799 18.908C4.40973 19.2843 4.71569 19.5903 5.09202 19.782C5.51984 20 6.0799 20 7.2 20H12.8C13.9201 20 14.4802 20 14.908 19.782C15.2843 19.5903 15.5903 19.2843 15.782 18.908C16 18.4802 16 17.9201 16 16.8V16M11.2 16H16.8C17.9201 16 18.4802 16 18.908 15.782C19.2843 15.5903 19.5903 15.2843 19.782 14.908C20 14.4802 20 13.9201 20 12.8V7.2C20 6.0799 20 5.51984 19.782 5.09202C19.5903 4.71569 19.2843 4.40973 18.908 4.21799C18.4802 4 17.9201 4 16.8 4H11.2C10.0799 4 9.51984 4 9.09202 4.21799C8.71569 4.40973 8.40973 4.71569 8.21799 5.09202C8 5.51984 8 6.07989 8 7.2V12.8C8 13.9201 8 14.4802 8.21799 14.908C8.40973 15.2843 8.71569 15.5903 9.09202 15.782C9.51984 16 10.0799 16 11.2 16Z" />
        </svg>
      </motion.button>
    );
  };

  const getTotalChallengesCount = () => {
    return challengesResponse?.total || 0;
  };

  const getLiveChallengesCount = () => {
    return (
      challengesResponse?.challenges.filter(
        (challenge) => challenge.status === "Live"
      ).length || 0
    );
  };

  const getUpcomingChallengesCount = () => {
    return (
      challengesResponse?.challenges.filter(
        (challenge) => challenge.status === "Upcoming"
      ).length || 0
    );
  };

  const getCompletedChallengesCount = () => {
    return (
      challengesResponse?.challenges.filter(
        (challenge) => challenge.status === "Completed"
      ).length || 0
    );
  };
  const now = new Date();

  const handleSafeEdit = (challenge: Challenge) => {
    const start = new Date(challenge.start_date);
    const end = new Date(challenge.end_date);

    if (start < now) {
      toast.error(
        "You cannot edit this challenge because its Start time has passed"
      );
      return;
    }
    if (end < now) {
      toast.error(
        "You cannot edit this challenge because its End time has passed"
      );
      return;
    }
    onEditChallenge(challenge); // allowed
  };

  return (
    <motion.div
      className="min-h-screen bg-gray-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Main Header */}
      <MainHeader onBackToDashboard={onBackToDashboard} />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <motion.button
                onClick={onBackToDashboard}
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors duration-200"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Back to Dashboard
              </motion.button>
              <motion.h1
                className="text-3xl font-bold text-gray-900"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Challenge Management
              </motion.h1>
              <motion.p
                className="mt-2 text-sm text-gray-600"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                Create, manage, and monitor trading challenges
              </motion.p>
            </div>
            <motion.button
              onClick={onCreateChallenge}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Create Challenge
            </motion.button>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Challenges
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {getTotalChallengesCount()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Live Challenges
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {getLiveChallengesCount()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Upcoming
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {getUpcomingChallengesCount()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Completed
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {getCompletedChallengesCount()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div>
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by challenge name..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label
                  htmlFor="status-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Status
                </label>
                <select
                  id="status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Status</option>
                  <option value="Draft">Draft</option>
                  <option value="Upcoming">Upcoming</option>
                  <option value="Live">Live</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="type-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Type
                </label>
                <select
                  id="type-filter"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Types</option>
                  <option value="Flexible">Flexible</option>
                  <option value="Guaranteed">Guaranteed</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="category-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Category
                </label>
                <select
                  id="category-filter"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All Categories</option>
                  <option value="Equity">Equity</option>
                  <option value="F&O">F&O</option>
                  <option value="Commodities">Commodities</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="experience-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Experience
                </label>
                <select
                  id="experience-filter"
                  value={experienceFilter}
                  onChange={(e) => setExperienceFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Expert">Expert</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="risk-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Risk
                </label>
                <select
                  id="risk-filter"
                  value={riskFilter}
                  onChange={(e) => setRiskFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="guaranteed-filter"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Guaranteed
                </label>
                <select
                  id="guaranteed-filter"
                  value={guaranteedFilter}
                  onChange={(e) => setGuaranteedFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setStatusFilter("all");
                    setTypeFilter("all");
                    setCategoryFilter("all");
                    setExperienceFilter("all");
                    setRiskFilter("all");
                    setGuaranteedFilter("all");
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Challenges Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Challenges</h3>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : error ? (
            <div className="px-6 py-4 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          ) : !challengesResponse?.challenges ||
            challengesResponse.challenges.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No challenges found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new challenge.
              </p>
              <div className="mt-6">
                <button
                  onClick={onCreateChallenge}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  Create Challenge
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Challenge
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Challenge Name
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Challenge Type
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entry Fee
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prize Pool
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Participants
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {challengesResponse.challenges.map((challenge, index) => (
                      <React.Fragment key={challenge.id}>
                        <motion.tr
                          className="hover:bg-gray-50 transition-colors duration-150"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          whileHover={{
                            scale: 1.01,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            transition: { duration: 0.2 },
                          }}
                        >
                          <td className="px-2 py-4 whitespace-nowrap">
                            <button
                              onClick={() =>
                                setExpandedRows((prev) => ({
                                  ...prev,
                                  [challenge.id]: !prev[challenge.id],
                                }))
                              }
                              className="text-xs text-indigo-600 hover:underline focus:outline-none"
                            >
                              {expandedRows[challenge.id] ? "−" : "+"}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div
                              className="max-w-[150px] truncate"
                              title={challenge.name}
                            >
                              {challenge.name}
                            </div>
                          </td>
                          <td> {challenge.type}</td>
                          <td>{challenge.entry_fee}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {challenge.status}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(challenge.prize_pool)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDateRange(
                              challenge.start_date,
                              challenge.end_date
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {challenge.max_participants || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(challenge.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              <motion.button
                                onClick={() => onViewChallenge(challenge)}
                                className="text-indigo-600 hover:text-indigo-900 transition-colors duration-150"
                                title="View Challenge"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </motion.button>

                              {challenge.status === "Draft" && (
                                <motion.button
                                  onClick={() => handleSafeEdit(challenge)}
                                  className="text-yellow-600 hover:text-yellow-900 transition-colors duration-150"
                                  title="Edit Challenge"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                    />
                                  </svg>
                                </motion.button>
                              )}
                              {getCloneButton(challenge)}
                              {getStatusActionButton(challenge)}
                              {getStatusCancelButton(challenge)}
                              <motion.button
                                onClick={() => handleDeleteChallenge(challenge)}
                                className="text-red-600 hover:text-red-900 transition-colors duration-150"
                                title="Delete Challenge"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                        {expandedRows[challenge.id] && (
                          <tr>
                            <td colSpan={7} className="bg-gray-50 px-6 py-4">
                              <table className="min-w-full text-xs text-gray-700">
                                <tbody>
                                  <tr>
                                    <td className="font-semibold pr-2">
                                      Type:
                                    </td>
                                    <td>{challenge.type || "N/A"}</td>
                                    <td className="font-semibold pr-2">
                                      Category:
                                    </td>
                                    <td>{challenge.category || "N/A"}</td>
                                    <td className="font-semibold pr-2">
                                      Duration Tag:
                                    </td>
                                    <td>{challenge.duration_tag || "N/A"}</td>
                                  </tr>
                                  <tr>
                                    <td className="font-semibold pr-2">
                                      Experience Level:
                                    </td>
                                    <td>
                                      {challenge.experience_level || "N/A"}
                                    </td>
                                    <td className="font-semibold pr-2">
                                      Risk Appetite:
                                    </td>
                                    <td>{challenge.risk_appetite || "N/A"}</td>
                                    <td className="font-semibold pr-2">
                                      Is Guaranteed:
                                    </td>
                                    <td>
                                      {challenge.is_guaranteed ? "Yes" : "No"}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="font-semibold pr-2">
                                      Challenge Type:
                                    </td>
                                    <td>{challenge.challenge_type || "N/A"}</td>
                                    <td className="font-semibold pr-2">
                                      Stock Universe:
                                    </td>
                                    <td>
                                      {Array.isArray(
                                        challenge.stock_universe_ids
                                      ) &&
                                      challenge.stock_universe_ids.length > 0
                                        ? challenge.stock_universe ||
                                          challenge.stock_universe_ids.join(
                                            ", "
                                          )
                                        : "N/A"}
                                    </td>
                                    <td className="font-semibold pr-2">
                                      Info Box Text:
                                    </td>
                                    <td>{challenge.info_box_text || "N/A"}</td>
                                  </tr>
                                  <tr>
                                    <td className="font-semibold pr-2">
                                      Created By:
                                    </td>
                                    <td>{challenge.created_by || "N/A"}</td>
                                    <td className="font-semibold pr-2">
                                      Updated At:
                                    </td>
                                    <td>
                                      {challenge.updated_at
                                        ? formatDate(challenge.updated_at)
                                        : "N/A"}
                                    </td>
                                    <td></td>
                                    <td></td>
                                  </tr>
                                </tbody>
                              </table>
                              {/* Business Metrics for Guaranteed Challenge */}
                              {challenge.is_guaranteed && (
                                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                                  <div className="font-semibold mb-2 text-yellow-800">
                                    Business Metrics (Guaranteed Challenge)
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div>
                                      <span className="font-semibold">
                                        Breakeven Participants:{" "}
                                      </span>
                                      {challenge.entry_fee > 0
                                        ? Math.ceil(
                                            ((challenge.prize_pool || 0) +
                                              (challenge.platform_stake || 0)) /
                                              challenge.entry_fee
                                          )
                                        : "N/A"}
                                    </div>
                                    <div>
                                      <span className="font-semibold">
                                        Platform Profit/Loss:{" "}
                                      </span>
                                      {typeof challenge.entry_fee ===
                                        "number" &&
                                      typeof challenge.current_participants ===
                                        "number" &&
                                      typeof challenge.platform_stake ===
                                        "number" &&
                                      typeof challenge.prize_pool === "number"
                                        ? formatCurrency(
                                            challenge.platform_stake +
                                              challenge.entry_fee *
                                                challenge.current_participants -
                                              challenge.prize_pool
                                          )
                                        : "N/A"}
                                    </div>
                                    <div>
                                      <span className="font-semibold">
                                        Current Participants:{" "}
                                      </span>
                                      {challenge.current_participants}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {challengesResponse.total_pages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage(
                          Math.min(
                            challengesResponse.total_pages,
                            currentPage + 1
                          )
                        )
                      }
                      disabled={currentPage === challengesResponse.total_pages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{" "}
                        <span className="font-medium">
                          {(currentPage - 1) * perPage + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-medium">
                          {Math.min(
                            currentPage * perPage,
                            challengesResponse.total
                          )}
                        </span>{" "}
                        of{" "}
                        <span className="font-medium">
                          {challengesResponse.total}
                        </span>{" "}
                        results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        {Array.from(
                          {
                            length: Math.min(5, challengesResponse.total_pages),
                          },
                          (_, i) => {
                            const page =
                              Math.max(
                                1,
                                Math.min(
                                  challengesResponse.total_pages - 4,
                                  currentPage - 2
                                )
                              ) + i;
                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  page === currentPage
                                    ? "z-10 bg-indigo-50 border-indigo-500 text-indigo-600"
                                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                {page}
                              </button>
                            );
                          }
                        )}
                        <button
                          onClick={() =>
                            setCurrentPage(
                              Math.min(
                                challengesResponse.total_pages,
                                currentPage + 1
                              )
                            )
                          }
                          disabled={
                            currentPage === challengesResponse.total_pages
                          }
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Complete Challenge Confirmation Modal */}
        {showCompleteModal && challengeToComplete && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Complete Challenge
                </h3>

                <div className="mb-6">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to complete the challenge{" "}
                    <span className="font-semibold text-purple-600">
                      "{challengeToComplete.name}"
                    </span>
                    ?
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    This action will finalize the challenge and calculate
                    winners. This cannot be undone.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancelComplete}
                    disabled={actionLoading === challengeToComplete.id}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmComplete}
                    disabled={actionLoading === challengeToComplete.id}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === challengeToComplete.id ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Completing...
                      </div>
                    ) : (
                      "Complete Challenge"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Challenge Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          title="Delete Challenge"
          message={`Are you sure you want to delete the challenge "${challengeToDelete?.name}"?`}
          confirmText="Delete"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          loading={deleteLoading}
        />

        {/* Cancel Challenge Confirmation Modal */}
        <ConfirmationModal
          isOpen={showCancelModal}
          onConfirm={handleConfirmCancel}
          onCancel={handleCancelCancel}
          title="Cancel Challenge"
          message={`Are you sure you want to cancel the challenge "${challengeToCancel?.name}"?`}
          confirmText="Cancel Challenge"
          confirmButtonColor="bg-orange-600 hover:bg-orange-700"
          loading={cancelLoading}
        />

        {/* Publish Challenge Confirmation Modal */}
        <ConfirmationModal
          isOpen={showPublishModal}
          onConfirm={handleConfirmPublish}
          onCancel={handleCancelPublish}
          title="Publish Challenge"
          message={`Are you sure you want to publish the challenge "${challengeToPublish?.name}"?`}
          confirmText="Publish"
          confirmButtonColor="bg-green-600 hover:bg-green-700"
          loading={publishLoading}
        />

        {/* Start Challenge Confirmation Modal */}
        <ConfirmationModal
          isOpen={showStartModal}
          onConfirm={handleConfirmStart}
          onCancel={handleCancelStart}
          title="Start Challenge"
          message={`Are you sure you want to start the challenge "${challengeToStart?.name}"?`}
          confirmText="Start"
          confirmButtonColor="bg-blue-600 hover:bg-blue-700"
          loading={startLoading}
        />
      </div>
    </motion.div>
  );
}
